import React, { useState } from 'react';
import SEO from './SEO';
import './LocalLlmPage.css';

const ENTERPRISE_ENDPOINT = 'https://audit.ubotika.site/enterprise';

const ROLES = ['Managing partner', 'IT director', 'General counsel', 'Operations / COO', 'Other'];
const SIZES = ['1–10 attorneys', '11–50 attorneys', '51–150 attorneys', '150+ attorneys'];

// Lead capture for a considered enterprise purchase. A firm evaluating an
// air-gapped appliance won't cold-dial a number — and our phone line demos the
// AI receptionist, which is a different product. This posts to /enterprise,
// which only emails the owner (never the auto-audit pipeline).
function PilotForm() {
  const [firm, setFirm] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [size, setSize] = useState('');
  const [note, setNote] = useState('');
  const [hp, setHp] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (hp) return;
    if (!firm.trim()) { setErrorMsg('Please enter your firm name.'); return; }
    if (!/^\S+@\S+\.\S+$/.test(email)) { setErrorMsg('Please enter a valid work email.'); return; }

    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch(ENTERPRISE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firm: firm.trim(),
          name: name.trim(),
          email: email.trim(),
          role,
          size,
          note: note.trim(),
          source: 'localllm',
        }),
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMsg('Could not send. Please email hello@ubotika.com directly.');
    }
  };

  if (status === 'success') {
    return (
      <div className="llm-form llm-form--done">
        <h3>Request received.</h3>
        <p>
          We&apos;ll reply from hello@ubotika.com within one business day with the
          technical brief and proposed pilot scope — no sales sequence.
        </p>
      </div>
    );
  }

  return (
    <form className="llm-form" onSubmit={onSubmit} noValidate>
      <h3>Request a pilot briefing</h3>
      <p className="llm-form-lede">
        Sent straight to us. We reply with the technical brief, the isolation test your
        IT director can run, and a scoped pilot.
      </p>

      <div className="llm-field">
        <label htmlFor="llm-firm">Firm name</label>
        <input id="llm-firm" type="text" value={firm} onChange={(e) => setFirm(e.target.value)} required />
      </div>

      <div className="llm-field-row">
        <div className="llm-field">
          <label htmlFor="llm-name">Your name</label>
          <input id="llm-name" type="text" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </div>
        <div className="llm-field">
          <label htmlFor="llm-email">Work email</label>
          <input id="llm-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
        </div>
      </div>

      <div className="llm-field-row">
        <div className="llm-field">
          <label htmlFor="llm-role">Your role</label>
          <select id="llm-role" value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="">Select…</option>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="llm-field">
          <label htmlFor="llm-size">Firm size</label>
          <select id="llm-size" value={size} onChange={(e) => setSize(e.target.value)}>
            <option value="">Select…</option>
            {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="llm-field">
        <label htmlFor="llm-note">What are you hoping to use it for? <span>(optional)</span></label>
        <textarea id="llm-note" rows="3" value={note} onChange={(e) => setNote(e.target.value)} />
      </div>

      <input
        type="text"
        name="website_url_confirm"
        tabIndex="-1"
        autoComplete="off"
        aria-hidden="true"
        className="llm-hp"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
      />

      {errorMsg && <p className="llm-form-error">{errorMsg}</p>}

      <button type="submit" className="llm-cta" disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending…' : 'Request the briefing'}
      </button>
      <p className="llm-form-fine">
        No newsletter, no sequence. Prefer email? <a href="mailto:hello@ubotika.com?subject=On-premises%20LLM%20pilot">hello@ubotika.com</a>
      </p>
    </form>
  );
}

// PRIVATE page. Not indexed, not in the sitemap, not linked from the site —
// reachable only by direct URL, matching the printed brochure handed to firms.

// ── FILL THESE IN ────────────────────────────────────────────────────────────
// The brochure ships with [X] placeholders. Edit here and every mention updates.
const SPEC = {
  installWindow: '[X] weeks',       // e.g. '3 weeks'
  footprintU: '[X]U',               // e.g. '4U'
  concurrentUsers: '[X]',           // e.g. '25'
  supportResponse: '[X]-hour',      // e.g. '4-hour'
  pilotWeeks: '[X]',                // e.g. '2'
  pilotAttorneys: '[X]',            // e.g. '5'
  revision: '[X]',                  // brochure revision, e.g. '1.0'
};
// ─────────────────────────────────────────────────────────────────────────────

const PROBLEMS = [
  "Client data sent to a commercial AI provider leaves the firm's control — governed by someone else's systems and terms.",
  'Retention windows, training-use carve-outs, and subprocessor lists vary between providers — and they change.',
  'Some clients and outside-counsel guidelines now restrict third-party AI use on their matters.',
];

const INSTALL = [
  {
    title: 'Open-weight models, local',
    body: "DeepSeek V4-Flash, Llama, Qwen — running on the appliance's GPUs.",
  },
  {
    title: 'Private web UI, LAN',
    body: 'A browser on the firm network. No client software, no accounts with us.',
  },
  {
    title: 'No outbound path',
    body: 'Verifiable at your firewall and on the box during the pilot.',
  },
  {
    title: 'Search your own files',
    body: 'We index the shares you designate; indexes stay on firm storage.',
  },
];

const SUBSCRIPTION = [
  'Hardware, sized and replaced by us',
  'On-site installation with your IT',
  'Model deployment and indexing',
  'Updates, on your approval',
  'Monitoring — metrics only',
  `Support, ${SPEC.supportResponse} response`,
];

const LIMITATIONS = [
  'Local open-weight models are strong but not identical to frontier cloud models; the hardest reasoning and long-context tasks show it.',
  'Throughput is sized to concurrent users. We scope hardware to firm size; heavy simultaneous use queues rather than scales on demand.',
  "This does not replace the firm's document management system. Your DMS remains the system of record.",
  "It is not a substitute for legal review of AI output. Every result still needs a lawyer's judgment.",
];

function LocalLlmPage() {
  return (
    <>
      <SEO
        title="Private On-Premises AI for Law Firms | UBOTIKA"
        description="An air-gapped LLM appliance installed in your office. No outbound network, firm-owned storage, month-to-month managed service."
        path="/localllm"
        noindex
      />
      <main id="main-content" className="llm-page">
        <div className="llm-inner">
          {/* ── Top bar ── */}
          <header className="llm-topbar">
            <div className="llm-topbar-row">
              <div className="llm-brand">
                <img src="/ubotika-logo.png" alt="" aria-hidden="true" />
                <span>UBOTIKA</span>
              </div>
              <div className="llm-topbar-meta">
                Private AI for law firms · ubotika.com/localllm
              </div>
            </div>
          </header>

          {/* ── Hero ── */}
          <section className="llm-hero">
            <p className="llm-eyebrow">On-premises · Air-gapped · Managed</p>
            <h1>A private AI system that never leaves your office.</h1>
            <p className="llm-hero-sub">
              Firm-owned hardware, installed in your office. No internet connection. No
              third-party model provider. The models run on the appliance; your documents
              and query history stay on storage the firm owns.
            </p>

            <dl className="llm-specs">
              <div className="llm-spec">
                <dt>Outbound network</dt>
                <dd>None</dd>
              </div>
              <div className="llm-spec">
                <dt>Access path</dt>
                <dd>Firm LAN only</dd>
              </div>
              <div className="llm-spec">
                <dt>Commitment</dt>
                <dd>Month-to-month</dd>
              </div>
              <div className="llm-spec">
                <dt>Install window</dt>
                <dd>{SPEC.installWindow}</dd>
              </div>
            </dl>
          </section>

          {/* ── 01 problem / 02 install ── */}
          <section className="llm-section">
            <div className="llm-two">
              <div>
                <p className="llm-section-label">01 / The problem</p>
                <h2>Where the confidentiality question sits</h2>
                <ol className="llm-numbered">
                  {PROBLEMS.map((p) => <li key={p}>{p}</li>)}
                </ol>
                <p className="llm-disclaimer">
                  We make no claim about how courts have treated any of this. An
                  on-premises system removes the third-party transfer from the analysis.
                </p>
              </div>

              <div>
                <p className="llm-section-label">02 / What we install</p>
                <h2>One appliance, on your premises</h2>
                <div className="llm-cards">
                  {INSTALL.map((c) => (
                    <article className="llm-card" key={c.title}>
                      <h3>{c.title}</h3>
                      <p>{c.body}</p>
                    </article>
                  ))}
                </div>
                <p className="llm-footnote">
                  Footprint {SPEC.footprintU} · sized for {SPEC.concurrentUsers} concurrent
                  users · firm provides rack space, power, LAN port, storage.
                </p>
              </div>
            </div>
          </section>

          {/* ── 03 architecture ── */}
          <section className="llm-section">
            <p className="llm-section-label">03 / Data separation architecture</p>
            <div className="llm-arch">
              <div className="llm-box llm-box--ours">
                <p className="llm-box-label">Box A — UBOTIKA-owned</p>
                <h3>UBOTIKA appliance</h3>
                <p>
                  Model weights · inference software · GPU hardware, OS, serving stack ·
                  no document or query storage.
                </p>
              </div>

              <div className="llm-gap" aria-hidden="true">
                <span className="llm-gap-label">LAN only</span>
                <span className="llm-gap-rule" />
                <span className="llm-gap-note">no egress</span>
              </div>

              <div className="llm-box llm-box--firm">
                <p className="llm-box-label">Box B — Firm-owned</p>
                <h3>Firm-owned storage</h3>
                <p>
                  All documents · all query history · all search indexes · encrypted at
                  rest, firm holds the keys.
                </p>
              </div>
            </div>
            <p className="llm-arch-note">
              If the engagement ends, the appliance is removed and firm data remains
              entirely with the firm — documents, history, and indexes are already on your
              storage, so there is nothing to migrate back.
            </p>
          </section>

          {/* ── 04 subscription / 05 limitations ── */}
          <section className="llm-section">
            <div className="llm-two">
              <div>
                <p className="llm-section-label">04 / The subscription</p>
                <h2>A managed service, month-to-month</h2>
                <div className="llm-sub-grid">
                  <ol className="llm-numbered">
                    {SUBSCRIPTION.slice(0, 3).map((s) => <li key={s}>{s}</li>)}
                  </ol>
                  <ol className="llm-numbered llm-numbered--cont" start="4">
                    {SUBSCRIPTION.slice(3).map((s) => <li key={s}>{s}</li>)}
                  </ol>
                </div>
                <p className="llm-footnote">
                  No hardware purchase and no capital request. One monthly fee, scoped to
                  the appliance your firm needs.
                </p>
              </div>

              <div className="llm-limits">
                <p className="llm-section-label">05 / Honest limitations</p>
                <ul>
                  {LIMITATIONS.map((l) => <li key={l}>{l}</li>)}
                </ul>
              </div>
            </div>
          </section>

          {/* ── 06 pilot ── */}
          <section className="llm-pilot" id="pilot">
            <div>
              <p className="llm-section-label">06 / Pilot</p>
              <h2>Evaluate it on your own network first</h2>
              <p>
                A {SPEC.pilotWeeks}-week pilot on one appliance with {SPEC.pilotAttorneys}{' '}
                attorneys and your IT director, who verifies the isolation. If it does not
                hold up, we remove the appliance and the engagement ends.
              </p>
              <ul className="llm-pilot-points">
                <li>Your IT director verifies there is no outbound path, on your firewall</li>
                <li>Documents and query history stay on firm storage throughout</li>
                <li>Month-to-month after the pilot — no capital request</li>
              </ul>
            </div>
            <PilotForm />
          </section>

          <footer className="llm-foot">
            <span>UBOTIKA · Los Angeles</span>
            <span>Air-gapped local LLM service · rev {SPEC.revision}</span>
          </footer>
        </div>
      </main>
    </>
  );
}

export default LocalLlmPage;
